/**
 * MP3 file-based sound effects. Each clip definition specifies a source file,
 * optional start offset / duration (for slicing long files), and a gain level.
 * Uses Web Audio API — shares nothing with the synthesised Sfx class but
 * respects the same mute / volume settings.
 */

export interface ClipDef {
  url: string;
  /** Start offset in seconds (default 0). */
  offset?: number;
  /** Playback duration in seconds. Omit to play until end of file. */
  duration?: number;
  /** Per-clip gain (0..1, default 0.8). */
  gain?: number;
}

/** Non-unit clips (buildings, turn-start). Unit clips are resolved via UNIT_FOLDER. */
const CLIP_LIB: Record<string, ClipDef> = {
  'select-barracks': { url: '/audio/buildings/barracks/selected-01.mp3', gain: 0.9 },
  'select-factory': { url: '/audio/factory-01.mp3', duration: 2.5, gain: 0.8 },
  'select-shipyard': { url: '/audio/buildings/shipyard/selected-01.mp3', duration: 2.0, gain: 0.8 },
  'turn-start': { url: '/audio/buildings/barracks/selected-01.mp3', gain: 0.9 },
  // Morse "incoming dispatch" cue, played when a scenario event message arrives.
  'message': { url: '/audio/morse-code-sound.mp3', duration: 3.0, gain: 0.7 },
  // Battle-window weapon / movement SFX. `duration` trims each clip to roughly
  // the on-screen action length (clips are NOT looped or stretched). One sound
  // is shared across several unit types (see battleFireClip / battleDeployClip
  // in TurnGameScene).
  'battle-fire-mg':         { url: '/audio/battle/machine-gun-fire.mp3',      duration: 1.5, gain: 1.1 },
  'battle-fire-bazooka':    { url: '/audio/battle/bazooka-fire.mp3',          duration: 1.5, gain: 1.1 },
  'battle-fire-cannon':     { url: '/audio/battle/cannon-fire.mp3',           duration: 1.5, gain: 1.1 },
  'battle-fire-heavy-arty': { url: '/audio/battle/heavy-artillery-fire.mp3',  duration: 1.5, gain: 1.1 },
  'battle-deploy-march':    { url: '/audio/battle/infantry-marching.mp3',     duration: 1.7, gain: 0.9 },
  'battle-deploy-truck':    { url: '/audio/battle/truck-driving.mp3',         duration: 1.7, gain: 0.9 },
  'battle-deploy-tank':     { url: '/audio/battle/tank-moving.mp3',           duration: 1.7, gain: 0.9 },
  'battle-deploy-cavalry':  { url: '/audio/battle/horses-galloping.mp3',      duration: 1.7, gain: 0.9 },
  // Battle-window HIT / death SFX, played once per killing volley by unit class
  // (see battleHitClip in TurnGameScene). Mechanical = tanks/vehicles/planes/ships
  // get the explosion; cavalry and foot troops get their own clips once recorded:
  //   'battle-hit-cavalry':  { url: '/audio/battle/hit-cavalry.mp3',  ... }
  //   'battle-hit-infantry': { url: '/audio/battle/hit-infantry.mp3', ... }
  'battle-hit-mechanical':  { url: '/audio/battle/hit-mechanical.mp3',        duration: 1.6, gain: 1.1 },
  // Falling-bomb whistle — played once when a bomber run releases its bombs
  // (see the 'bombing' branch of battleStrike). Plays to natural end so the
  // descending whistle covers the bombs' fall onto the target.
  'battle-bomb-falling':    { url: '/audio/battle/bomb-falling.mp3',          gain: 1.1 },
};

export type FileSfxName = keyof typeof CLIP_LIB;

export type SfxAction = 'selected' | 'move' | 'attack' | 'capture';

/** Map game unit type → audio folder under public/audio/units/. */
const UNIT_FOLDER: Record<string, string> = {
  'infantry': 'infantry',
  'infantry-bazooka': 'infantry',
  'recon': 'recon',
  'cavalry': 'cavalry',
  'jeep': 'jeep',
  'light-tank': 'lt-tank',
  'medium-tank': 'md-tank',
  'heavy-tank': 'hv-tank',
  'tank-destroyer': 'tank-destroyer',
  'apc': 'apc',
  'supply-truck': 'supply-truck',
  'medic': 'medic',
  'long-range-artillery': 'artillery',
  'anti-tank-artillery': 'at-gun',
  'aa-flakpanzer': 'aa-gun',
  'plane-fighter': 'fighter',
  'plane-bomber': 'bomber',
  'plane-transport': 'transport-plane',
  'ship-submarine': 'submarine',
  'ship-battleship': 'battleship',
  'ship-aircraft-carrier': 'aircraft-carrier',
  'ship-transport': 'ship-transport',
};

interface VoicePack {
  /** Per-unit-folder clip counts for each action. */
  folders: Record<string, Partial<Record<SfxAction, number>>>;
  /** Shared pool (audio/units[/<root>]/misc/<action>-NN.wav) drawn alongside folders. */
  misc: Partial<Record<SfxAction, number>>;
}

/**
 * Default (English) voice pack. Files are named `<action>-NN.wav` (1-based,
 * zero-padded). When a unit acts, candidates are drawn from BOTH the unit's
 * folder and the shared `misc/` pool for that action, so units automatically
 * share the generic lines on top of their signature ones.
 */
const DEFAULT_PACK: VoicePack = {
  folders: {
    'infantry':        { selected: 1, move: 2, attack: 2 },
    'cavalry':         { selected: 1, move: 1, attack: 1 },
    'recon':           { selected: 2, attack: 1 },
    'jeep':            { move: 1 },
    'lt-tank':         { move: 1 },
    'md-tank':         { attack: 1 },
    'hv-tank':         { selected: 1, attack: 1 },
    'tank-destroyer':  { selected: 1, attack: 2 },
    'at-gun':          { selected: 1, attack: 1 },
    'aa-gun':          { attack: 2 },
    'artillery':       { selected: 2, attack: 3 },
    'fighter':         { move: 1, attack: 1 },
    'bomber':          { move: 1, attack: 3 },
    'transport-plane': { move: 1, selected: 1 },
    'medic':           { selected: 1, move: 1 },
    'supply-truck':    { selected: 1, move: 1 },
    'submarine':       { selected: 1, attack: 2 },
  },
  misc: { selected: 4, move: 3, attack: 5, capture: 3 },
};

/**
 * Nation-specific voice packs, keyed by faction id (see turn/factions.ts).
 * A unit of a faction with a pack draws only from that pack (folder + its misc);
 * it falls back to the English DEFAULT_PACK only when the pack has no clip for
 * the action at all (which the misc pool always covers, so this is rare).
 */
const FACTION_PACKS: Record<string, VoicePack> = {
  polska: {
    folders: {
      'infantry':        { selected: 1 },
      'recon':           { selected: 1 },
      'cavalry':         { move: 1 },
      'supply-truck':    { selected: 1 },
      'hv-tank':         { selected: 1, attack: 1 },
      'md-tank':         { selected: 1, attack: 1 },
      'lt-tank':         { attack: 1 },
      'tank-destroyer':  { selected: 1, attack: 1 },
      'at-gun':          { selected: 1, attack: 1 },
      'aa-gun':          { attack: 2 },
      'artillery':       { attack: 3 },
      'fighter':         { move: 1, attack: 1 },
      'bomber':          { move: 1, attack: 1 },
      'transport-plane': { move: 1 },
      'medic':           { move: 1 },
      'submarine':       { selected: 1 },
    },
    misc: { selected: 2, move: 3, attack: 5, capture: 3 },
  },
};

/** Default gain per action when playing a unit SFX clip. */
const ACTION_GAINS: Record<SfxAction, number> = {
  selected: 0.45,
  move: 0.4,
  attack: 0.45,
  capture: 0.45,
};

const BASE_GAIN = 0.8;

export class FileSfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer | null>>();
  private activeSources = new Map<string, AudioBufferSourceNode>();
  /** Clips that were stopped before their async load finished. */
  private cancelled = new Set<string>();
  /** Cooldown tracking: last play timestamp per clip name/url. */
  private lastPlayTime = new Map<string, number>();
  /** Minimum ms between plays of the same clip (prevents overlap on rapid clicks). */
  private static readonly COOLDOWN_MS = 350;
  /** Shuffle-bag of not-yet-played indices per `<pack>/<folder>/<action>` group. */
  private idxBag = new Map<string, number[]>();
  /** Last index played per group — avoids a repeat across bag refills. */
  private lastIdxPick = new Map<string, number>();
  muted = false;
  private volume = 1;

  setMuted(m: boolean): void {
    this.muted = m;
    this.applyGain();
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyGain();
  }

  private applyGain(): void {
    if (!this.master) return;
    this.master.gain.value = this.muted ? 0 : BASE_GAIN * this.volume;
  }

  private ensure(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext
      || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.applyGain();
    this.master.connect(this.ctx.destination);
  }

  /** Preload buffers for given clip names so first play is instant. */
  preload(...names: FileSfxName[]): void {
    this.ensure();
    for (const name of names) {
      const def = CLIP_LIB[name];
      if (def) void this.loadBuffer(def.url);
    }
  }

  /** Play a named clip. Stops any previously playing instance of the same clip. */
  play(name: string): void {
    const def = CLIP_LIB[name];
    if (!def) return;
    this.playClip(name, def);
  }

  /**
   * Play a unit's SFX for a given action. Picks a random clip from the unit's
   * folder (avoiding consecutive repeats). Falls back to misc/<action> if the
   * unit has no clips for that action. No-op when neither has clips.
   */
  playUnitSfx(unitType: string, action: SfxAction, factionId?: string): void {
    const folder = UNIT_FOLDER[unitType];
    if (!folder) return;

    // Faction pack takes priority; fall back to the English default pack only
    // when the faction has no clip for this folder/action (folder AND misc).
    const factionPack = factionId ? FACTION_PACKS[factionId] : undefined;
    let root = factionId ?? '';
    let urls = factionPack ? this.buildCandidates(root, factionPack, folder, action) : [];
    if (urls.length === 0) {
      root = '';
      urls = this.buildCandidates(root, DEFAULT_PACK, folder, action);
    }
    if (urls.length === 0) return;

    const idx = this.pickRandomIdx(`${root || 'en'}/${folder}/${action}`, urls.length);
    const url = urls[idx - 1];
    this.playClip(url, { url, gain: ACTION_GAINS[action] });
  }

  /**
   * Candidate clip URLs for a unit action within a pack: the unit's folder
   * clips followed by the shared misc-pool clips for that action. `root` is ''
   * for the default (English) pack, or the faction id for a nation pack.
   */
  private buildCandidates(root: string, pack: VoicePack, folder: string, action: SfxAction): string[] {
    const base = root ? `/audio/units/${root}` : '/audio/units';
    const urls: string[] = [];
    const fc = pack.folders[folder]?.[action] ?? 0;
    for (let i = 1; i <= fc; i++) {
      urls.push(`${base}/${folder}/${action}-${i.toString().padStart(2, '0')}.wav`);
    }
    const mc = pack.misc[action] ?? 0;
    for (let i = 1; i <= mc; i++) {
      urls.push(`${base}/misc/${action}-${i.toString().padStart(2, '0')}.wav`);
    }
    return urls;
  }

  /** Stop a currently playing clip (or cancel a pending async load). */
  stop(name: string): void {
    this.cancelled.add(name);
    this.stopImmediate(name);
  }

  resume(): void {
    this.ensure();
    if (this.ctx && this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /**
   * Pick a 1-based clip index using a shuffle-bag: every index plays once
   * before any repeats, so the same line is never heard twice until the whole
   * pool is exhausted. On refill the first draw avoids repeating the last one.
   */
  private pickRandomIdx(groupKey: string, count: number): number {
    if (count <= 1) {
      this.lastIdxPick.set(groupKey, 1);
      return 1;
    }
    let bag = this.idxBag.get(groupKey);
    // Refill and reshuffle once the bag is exhausted.
    if (!bag || bag.length === 0) {
      bag = this.shuffledIndices(count);
      // Avoid back-to-back repeat across the refill: the next draw is bag[last].
      const last = this.lastIdxPick.get(groupKey);
      if (bag[bag.length - 1] === last) {
        [bag[bag.length - 1], bag[0]] = [bag[0], bag[bag.length - 1]];
      }
      this.idxBag.set(groupKey, bag);
    }
    const idx = bag.pop() as number;
    this.lastIdxPick.set(groupKey, idx);
    return idx;
  }

  /** Fisher-Yates shuffle of [1..count]. */
  private shuffledIndices(count: number): number[] {
    const arr = Array.from({ length: count }, (_, i) => i + 1);
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  /** Shared playback path used by play() and playUnitSfx(). */
  private playClip(key: string, def: ClipDef): void {
    this.ensure();
    if (!this.ctx || !this.master) return;
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    if (this.muted || this.volume <= 0) return;

    // Cooldown — skip if the same clip was played too recently
    const now = performance.now();
    const last = this.lastPlayTime.get(key) ?? 0;
    if (now - last < FileSfx.COOLDOWN_MS) return;
    this.lastPlayTime.set(key, now);

    this.stopImmediate(key);
    this.cancelled.delete(key);

    const cachedBuf = this.buffers.get(def.url);
    if (cachedBuf) {
      this.startSource(key, def, cachedBuf);
      return;
    }

    void this.loadBuffer(def.url).then((buf) => {
      if (!buf || this.cancelled.has(key)) {
        this.cancelled.delete(key);
        return;
      }
      if (this.muted || this.volume <= 0) return;
      this.startSource(key, def, buf);
    });
  }

  private stopImmediate(name: string): void {
    const src = this.activeSources.get(name);
    if (src) {
      try { src.stop(); } catch { /* */ }
      this.activeSources.delete(name);
    }
  }

  private startSource(name: string, def: ClipDef, buffer: AudioBuffer): void {
    if (!this.ctx || !this.master) return;

    const now = this.ctx.currentTime;
    const offset = def.offset ?? 0;
    const clipGain = def.gain ?? 0.8;

    const env = this.ctx.createGain();
    env.gain.setValueAtTime(clipGain, now);

    if (def.duration) {
      const fadeStart = now + Math.max(0, def.duration - 0.15);
      env.gain.setValueAtTime(clipGain, fadeStart);
      env.gain.linearRampToValueAtTime(0, now + def.duration);
    }
    env.connect(this.master);

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(env);

    if (def.duration) {
      src.start(now, offset, def.duration + 0.05);
    } else {
      src.start(0, offset);
    }

    src.onended = () => {
      this.activeSources.delete(name);
    };

    this.activeSources.set(name, src);
  }

  private loadBuffer(url: string): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(url);
    if (cached) return Promise.resolve(cached);

    const existing = this.loading.get(url);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const resp = await fetch(url);
        const data = await resp.arrayBuffer();
        const buf = await this.ctx!.decodeAudioData(data);
        this.buffers.set(url, buf);
        return buf;
      } catch (e) {
        console.warn(`[FileSfx] Failed to load ${url}:`, e);
        return null;
      } finally {
        this.loading.delete(url);
      }
    })();

    this.loading.set(url, promise);
    return promise;
  }
}
